<template>
  <div class="personal-infos">
    <div class="_profile-image">
      <div class="_avatar">
        <img
          :src="profileImage"
          alt=""
        >
        <input
          ref="fileinput"
          type="file"
          :style="{display: 'none'}"
          accept="image/*"
          @change="fileChanged"
        >
        <m-dialog
          v-model:value="showCropImage"
          :title="$t('accountSettings.setProfilePicture')"
          hide-footer
          keep-open-on-mask-click
          no-padding
        >
          <image-crop
            v-if="file !== null"
            class="_image-crop"
            :file="file"
            @cropped="uploadImage"
            @cancel="cancelImageCrop"
            @wrong-file-type="handleWrongFileType"
          />
        </m-dialog>
      </div>
      <div class="_upload-btn">
        <m-btn
          hide-border
          small
          light
          @click="triggerInput"
        >
          {{ $t('personalInfos.uploadImage') }}
        </m-btn>
      </div>
    </div>
    <div class="_form">
      <div class="_item">
        <div class="_label">
          {{ $t('personalInfos.firstNameLabel') }}
        </div>
        <m-tooltip
          :force-show="firstNameField.hasError"
          :relocate-key="firstNameField.value"
          placement="left"
        >
          <m-text-field
            v-model:value="firstNameField.value"
            :has-error="firstNameField.hasError"
            auto-focus
            @blur="firstNameField.onBlur"
          />
          <template
            v-if="firstNameField.hasError"
            #title
          >
            {{ firstNameField.error }}
          </template>
        </m-tooltip>
      </div>
      <div class="_item">
        <div class="_label">
          {{ $t('personalInfos.lastNameLabel') }}
        </div>
        <m-tooltip
          :force-show="lastNameField.hasError"
          :relocate-key="lastNameField.value"
          placement="left"
        >
          <m-text-field
            v-model:value="lastNameField.value"
            :has-error="lastNameField.hasError"
            @blur="lastNameField.onBlur"
            @keydown.enter="update"
          />
          <template
            v-if="lastNameField.hasError"
            #title
          >
            {{ lastNameField.error }}
          </template>
        </m-tooltip>
      </div>
      <div
        v-if="showPassword"
        class="_item"
      >
        <div class="_label">
          {{ $t('personalInfos.passwordLabel') }}
        </div>
        <m-input-password
          v-model:value="password"
          autocomplete="new-password"
          @keydown.enter="update"
        />
      </div>
      <m-btn
        color="black"
        :disabled="disabled"
        block
        :loading="loading"
        class="_action"
        @click="update"
      >
        <template v-if="!isLastStep">
          {{ $t('onboarding.continue') }}
        </template>
        <template v-else>
          {{ $t('onboarding.launchMooncamp') }}
        </template>
      </m-btn>
    </div>
  </div>
</template>

<script>
import ImageCrop from '@/components/ImageCrop.vue';
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useMe from '@/composables/me';
import { DateTime } from 'luxon';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { logCatch } from '@/lib/logger/logger';
import { ref } from 'vue';
import { usePersonNameField } from '@/composables/field-validation';

export default {
  name: 'PersonalInfos',
  props: {
    isLastStep: {
      type: Boolean,
      default: false,
    },
  },
  components: { ImageCrop },
  setup() {
    const { uploadProfileImage, loggedInUser, updateUser, updateUserLoading } = useLoggedInUser();
    const { loggedInUserAccount } = useLoggedInUserAccount();
    const { me } = useMe();

    const firstNameField = usePersonNameField(ref(''));
    const lastNameField = usePersonNameField(ref(''));

    return {
      me,
      loggedInUserAccount,
      updateUser,
      loading: updateUserLoading,
      uploadProfileImage,
      loggedInUser,
      firstNameField,
      lastNameField,
    };
  },
  data() {
    return {
      password: '',
      file: null,
      change: 0,
      showCropImage: false,
    };
  },
  computed: {
    passwordValid() {
      if (!this.showPassword) {
        return true;
      }

      return this.password !== '' && this.password.length >= 8;
    },
    showPassword() {
      if (!isNullOrUndefined(this.me?.passwordUpdatedAt)) {
        return false;
      }
      if (this.loggedInUserAccount.accountSettings.restrictPasswordLogin) {
        return false;
      }
      // TODO(M5): replace the ?setPassword query heuristic with the auth method
      // on the session (password vs social/SAML), once the session carries how
      // the identity authenticated.
      return this.$route.query.setPassword !== undefined;
    },
    disabled() {
      return !this.firstNameField.isValid || !this.lastNameField.isValid || !this.passwordValid;
    },
    profileImage() {
      if (this.loggedInUser.profileImage === null) {
        return new URL('@/assets/img/profile-image.svg', import.meta.url).href;
      }

      return this.loggedInUser.profileImage.getURL;
    },
  },
  methods: {
    update() {
      this.firstNameField.onSubmit();
      this.lastNameField.onSubmit();
      if (this.disabled) {
        return;
      }
      const toUpdate = {
        uid: this.loggedInUser.uid,
        firstName: this.firstNameField.value,
        lastName: this.lastNameField.value,
        personalInfosCompleted: DateTime.local().toISO(),
      };
      if (this.password !== '') {
        toUpdate.identity = { uid: this.loggedInUser.identity.uid, password: this.password };
      }

      this.updateUser(toUpdate, null, { optimistic: false }).catch(() => {
        this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
      });
    },
    triggerInput() {
      this.$refs.fileinput.click();
    },
    uploadImage(blob) {
      this.uploadProfileImage(blob)
        .then(() => {
          this.change += 1;
          this.$forceUpdate();
          this.file = null;
        }).catch(logCatch(() => {
          this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
        })).finally(() => { this.showCropImage = false; });
    },
    cancelImageCrop() {
      this.showCropImage = false;
      this.file = null;
    },
    handleWrongFileType() {
      this.showCropImage = false;
      this.file = null;
      this.$showSnackbar({ color: 'error', message: this.$t('error.notAnImage') });
    },
    fileChanged(event) {
      if (event.target.files.length === 0) {
        return;
      }

      this.file = event.target.files[0];
      this.showCropImage = true;
      this.$refs.fileinput.value = '';
    },
  },
  watch: {
    showCropImage(newValue) {
      if (!newValue) {
        this.file = null;
      }
    },
  },
  created() {
    this.firstNameField.value = this.loggedInUser.firstName;
    this.lastNameField.value = this.loggedInUser.lastName;
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  @import "shared/assets/scss/box-shadow";

  .personal-infos {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    ._profile-image {
      display: flex;
      flex-direction: column;
      justify-content: center;

      ._avatar {
        margin-bottom: 1.6rem;
        text-align: center;

        img {
          width: 6.4rem;
          height: 6.4rem;
          border-radius: 50%;

          @include box_shadow(1);
        }
      }
    }

    ._form {
      width: 100%;
      max-width: 28rem;

      ._item {
        margin-bottom: 1.6rem;

        &:last-of-type {
          margin-bottom: 2.8rem;
        }

        ._label {
          font-size: $font-size-3;
          color: $font-color-secondary;
        }
      }
    }
  }
</style>
